import { Dispatch, useCallback, useContext, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { knmTryOpenSignModal } from 'KnmPackage/MainModule/actions/knmActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { decisionTryOpenSignModal } from 'DecisionPackage/MainModule/actions/decisionActions';
import { setDirtyAgreementGuid } from 'CorePackage/HeaderModule/actions/headerActions';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmOnSaveButtonClick } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionOnSaveButtonClick } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getDirtyAgreementGuid } from 'CorePackage/HeaderModule/reducers/headerReducer';

import styles from './SignButton.module.scss';

interface ISignButtonProps {
  agreement: IErknmFormAgreement;
  isDirtyForm: boolean;
  isDirtyBySigned: boolean;
  isDirtyBySignedWithoutViolationsFixed: boolean;
}

interface ISignButtonStoreProps {
  onDecisionOnSaveButtonClick: (() => void) | undefined;
  onKnmOnSaveButtonClick: (() => void) | undefined;
  dirtyAgreementGuid: string | undefined;
}

const SignButtonWithoutStore = ({
  agreement,
  onKnmOnSaveButtonClick,
  onDecisionOnSaveButtonClick,
  dirtyAgreementGuid,
  isDirtyForm,
  isDirtyBySigned,
  isDirtyBySignedWithoutViolationsFixed,
}: ISignButtonProps & ISignButtonStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { isDecision } = useContext(AgreementsContext);

  const onClickButtonSign = useCallback(() => {
    if (agreement.guid) {
      if (!isDecision) {
        dispatch(knmTryOpenSignModal());
      } else {
        dispatch(decisionTryOpenSignModal());
      }
    }
  }, [agreement, isDecision]);

  useEffect(() => {
    if (!agreement.isNew && agreement.signed && agreement.guid) {
      dispatch(setDirtyAgreementGuid(isDirtyBySignedWithoutViolationsFixed ? agreement.guid : undefined));
    }

    return () => {
      if (dirtyAgreementGuid === agreement.guid) {
        dispatch(setDirtyAgreementGuid(undefined));
      }
    };
  }, [isDirtyBySigned, dirtyAgreementGuid, isDirtyBySignedWithoutViolationsFixed]);

  const onClickButtonSave = useCallback(() => {
    if (isDecision) {
      onDecisionOnSaveButtonClick && onDecisionOnSaveButtonClick();
    } else {
      onKnmOnSaveButtonClick && onKnmOnSaveButtonClick();
    }
  }, [isDecision, onKnmOnSaveButtonClick, onDecisionOnSaveButtonClick]);

  let buttonTitle = '';
  let isButtonDisabled = false;
  let callback = () => {};

  if (agreement.signed) {
    if (isDirtyBySigned) {
      buttonTitle = 'Сохранить соглашение';
      callback = onClickButtonSave;
    } else {
      buttonTitle = 'Соглашение подписано';
      isButtonDisabled = true;
    }
  } else if (agreement.isNew || isDirtyForm) {
    buttonTitle = 'Сохранить соглашение';
    callback = onClickButtonSave;
  } else {
    buttonTitle = 'Подписать';
    callback = onClickButtonSign;
  }

  return (
    <Button
      color="primary"
      text={buttonTitle}
      id="saveButton"
      type={'button'}
      disabled={isButtonDisabled}
      onClick={callback}
      className={styles.ButtonSign}
    />
  );
};

const mapStateToProps = (state: IRootState): ISignButtonStoreProps => ({
  onDecisionOnSaveButtonClick: getDecisionOnSaveButtonClick(state),
  onKnmOnSaveButtonClick: getKnmOnSaveButtonClick(state),
  dirtyAgreementGuid: getDirtyAgreementGuid(state),
});

export const SignButton = connect(mapStateToProps)(SignButtonWithoutStore);
